// src/plugins/sales-forecast/components/ConstructionPhaseControl.tsx
import React, { useMemo } from "react";
import { Label } from "../../../components/ui/label";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export type ConstructionPhase =
  | "Plans"
  | "Pad"
  | "Footing"
  | "Initial Backfill"
  | "Plumbing Rough"
  | "Slab"
  | "Frame Delay"
  | "Walls & Joists"
  | "Rafters & Cornice"
  | "Decking"
  | "Roofing"
  | "Plumbing Top"
  | "HVAC Rough"
  | "Electrical Rough"
  | "Frame Punch"
  | "Insulation"
  | "Sheetrock"
  | "Wood Floors"
  | "Tile"
  | "Trim"
  | "Paint"
  | "Countertops"
  | "Fixtures"
  | "Final Clean"
  | "Punchout"
  | "Complete"
  | "Pre-Inspections"
  | "Brick"
  | "Stone"
  | "Flatwork"
  | "Rough Grade"
  | "Sprinkler"
  | "Sod"
  | "TU Grade"
  | "Yard Complete";

export type PhaseGroups = Record<string, ConstructionPhase[]>;

interface ConstructionPhaseControlProps {
  /** Source-of-truth selected RAW phases */
  value: ConstructionPhase[];
  /** Called with next RAW phase selection */
  onChange: (phases: ConstructionPhase[]) => void;

  /** Optional: UI mode */
  mode?: "raw" | "grouped";
  /** Optional: raw phase options to display (defaults to PHASE_OPTIONS) */
  phaseOptions?: ConstructionPhase[];
  /** Optional: phase groups map (only used in grouped mode) */
  phaseGroups?: PhaseGroups;
}

const PHASE_OPTIONS: ConstructionPhase[] = [
  "Plans",
  "Pad",
  "Footing",
  "Initial Backfill",
  "Plumbing Rough",
  "Slab",
  "Frame Delay",
  "Walls & Joists",
  "Rafters & Cornice",
  "Decking",
  "Roofing",
  "Plumbing Top",
  "HVAC Rough",
  "Electrical Rough",
  "Frame Punch",
  "Insulation",
  "Sheetrock",
  "Wood Floors",
  "Tile",
  "Trim",
  "Paint",
  "Countertops",
  "Fixtures",
  "Final Clean",
  "Punchout",
  "Complete",
  "Pre-Inspections",
  "Brick",
  "Stone",
  "Flatwork",
  "Rough Grade",
  "Sprinkler",
  "Sod",
  "TU Grade",
  "Yard Complete",
];

// Default selected phases
const DEFAULT_SELECTED_PHASES: ConstructionPhase[] = [
  "Plans",
  "Pad",
  "Footing",
  "Initial Backfill",
  "Plumbing Rough",
  "Slab",
  "Frame Delay",
];

function uniq<T>(xs: T[]): T[] {
  return Array.from(new Set(xs));
}

export const ConstructionPhaseControl: React.FC<ConstructionPhaseControlProps> = ({
  value,
  onChange,
  mode = "raw",
  phaseOptions,
  phaseGroups,
}) => {
  const theme = useSystemTheme();

  const selectedRaw: ConstructionPhase[] =
    value && value.length ? value : DEFAULT_SELECTED_PHASES;

  const selectedSet = useMemo(() => new Set(selectedRaw), [selectedRaw]);

  const rawOptions = phaseOptions && phaseOptions.length ? phaseOptions : PHASE_OPTIONS;

  const toggleRaw = (phase: ConstructionPhase) => {
    const next = selectedSet.has(phase)
      ? selectedRaw.filter((p) => p !== phase)
      : uniq([...selectedRaw, phase]);
    onChange(next);
  };

  const toggleGroup = (groupKey: string) => {
    const groupPhases = (phaseGroups?.[groupKey] ?? []).filter(Boolean);
    if (!groupPhases.length) return;

    const allOn = groupPhases.every((p) => selectedSet.has(p));
    let next: ConstructionPhase[];

    if (allOn) {
      // Turn OFF all phases in this group
      const off = new Set(groupPhases);
      next = selectedRaw.filter((p) => !off.has(p));
    } else {
      // Turn ON all phases in this group
      next = uniq([...selectedRaw, ...groupPhases]);
    }

    onChange(next);
  };

  const GroupRow = ({ groupKey }: { groupKey: string }) => {
    const phases = (phaseGroups?.[groupKey] ?? []).filter(Boolean);
    const selectedCount = phases.reduce((acc, p) => acc + (selectedSet.has(p) ? 1 : 0), 0);
    const allOn = phases.length > 0 && selectedCount === phases.length;

    const id = `phase-group-${groupKey}`;

    return (
      <div
        key={groupKey}
        style={{
          breakInside: "avoid",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          width: "100%",
          marginBottom: "0.5rem",
          color: "var(--text-color)",
        }}
      >
        <input
          id={id}
          type="checkbox"
          checked={allOn}
          onChange={() => toggleGroup(groupKey)}
          style={{
            accentColor: "var(--accent-color)",
            cursor: "pointer",
            width: "1rem",
            height: "1rem",
          }}
        />
        <Label
          htmlFor={id}
          style={{
            color: "var(--text-color)",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1.1,
          }}
        >
          {groupKey}{" "}
          <span style={{ opacity: 0.75 }}>
            ({selectedCount}/{phases.length})
          </span>
        </Label>
      </div>
    );
  };

  return (
    <div className={`construction-phase-control theme-${theme}`}>
      <div
        className="max-h-[45vh] overflow-y-auto p-3 rounded-lg"
        style={{
          backgroundColor: "var(--subcard-bg)",
          border: "1px solid var(--border-color)",
        }}
      >
        <h3 className="text-base font-semibold mb-3" style={{ color: "var(--text-color)" }}>
          {mode === "grouped" ? "Phase Groups" : "Construction Phases"}
        </h3>

        <div style={{ columnWidth: "12.5rem", columnGap: "1.25rem" }}>
          {mode === "grouped" ? (
            Object.keys(phaseGroups ?? {}).map((k) => <GroupRow key={k} groupKey={k} />)
          ) : (
            rawOptions.map((phase) => {
              const id = `phase-${phase}`;
              return (
                <div
                  key={phase}
                  style={{
                    breakInside: "avoid",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    width: "100%",
                    marginBottom: "0.5rem",
                    color: "var(--text-color)",
                  }}
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={selectedSet.has(phase)}
                    onChange={() => toggleRaw(phase)}
                    style={{
                      accentColor: "var(--accent-color)",
                      cursor: "pointer",
                      width: "1rem",
                      height: "1rem",
                    }}
                  />
                  <Label
                    htmlFor={id}
                    style={{
                      color: "var(--text-color)",
                      cursor: "pointer",
                      userSelect: "none",
                      lineHeight: 1.1,
                    }}
                  >
                    {phase}
                  </Label>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};